import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
  Accordion,
  Banner,
  Box,
  Button,
  Card,
  CloseButton,
  Heading,
  Image,
  Layout,
  List,
  Page,
  Paragraph,
  Radio,
  Text,
  TextButton,
} from '@zillow/constellation-10';
import { IconChevronLeftFilled } from '@zillow/constellation-icons';

import { ZillowInsuranceServicesLogo } from '@zillow/zrs-comet';

import { RMWContext } from '../../../providers/RMWContext';

import { useSelector } from '../../../selectors/useSelector';
import { AppSelectors } from '../../../selectors';

import numberUtils from '../../../utils/numberUtils';
import PropertiesActions from '../../../actions/PropertiesActions';
import SpinnerContainer from '../../SpinnerContainer';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import OptInCSEvents from '../../../model/ClickstreamEvent/RentalProtection/OptInCSEvents';
import ModalFooter from '../../Modal/ModalFooter';

import { useCompleteLightweightOptIn } from '../../../hooks/queries/rentalProtection';
import RentalProtectionHowDoesItWorkModal from '../../Modal/RentalProtectionHowDoesItWorkModal';
import RentalProtectionWhatsCoveredModal from '../../Modal/RentalProtectionWhatsCoveredModal';
import RentalProtectionLearnMoreModal from '../../Modal/RentalProtectionLearnMoreModal';
import PropertyDamageIcon from '../../../images/rentalProtection/property-damage.svg';
import FolderIcon from '../../../images/rentalProtection/folder-with-paperclip.svg';
import HomeSparkleIcon from '../../../images/HomeSparkle.svg';
import BackgroundImage from '../../../images/rentalProtection/choose_coverage_level_bg_desktop.svg';
import BackgroundImageMweb from '../../../images/rentalProtection/choose_coverage_level_bg_mobile.svg';
import { getIsLowFrictionEnabledFromProperty } from '../../../utils/rentalProtectionUtils';
import LeaseQualificationAccordion from '../components/LeaseQualificationAccordion';

import type { PremiumCoverage } from '../../../model/RentGuarantee';
import { updateLocation } from '../../../slices/app';

type Props = {
  listingAlias: string;
};

const CoverageLevelSelection = ({ listingAlias }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { paths } = useContext(RMWContext);

  const {
    currentProperty: {
      rentalProtection: {
        premiumCoverages,
        additionalDetails: {
          urls: { faq, coverageSummary: coverageSummaryUrl },
        },
      },
    },
    currentProperty,
  } = useSelector((state) => state.properties);

  const user = useSelector((state) => state.user);
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const { isLoading, data: propertyDetailsData } = useGetListingDetails(listingAlias);
  const { mutateAsync: completeLightweightOptIn } = useCompleteLightweightOptIn();
  const [hasApiError, setHasApiError] = useState<boolean>(false);
  const [selectedPremiumCoverageOption, setSelectedPremiumCoverageOption] = useState<PremiumCoverage>();
  const [showWhatsCoveredModal, setShowWhatsCoveredModal] = useState<boolean>(false);
  const [showHowItWorksModal, setShowHowItWorksModal] = useState<boolean>(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState<boolean>(false);

  const isLowFrictionEnabled = useMemo(
    // @ts-expect-error
    () => getIsLowFrictionEnabledFromProperty(propertyDetailsData),
    [propertyDetailsData],
  );

  useEffect(() => {
    OptInCSEvents.COVERAGE_SELECTION.ViewCoverageSelectionPage(csEventParams);
  }, []);

  const getDashboardUrl = () => {
    return `${paths.propertiesPage}/${listingAlias}/dashboard/overview`;
  };

  const csEventParams = useMemo(() => ({ user, currentProperty }), [user, currentProperty]);

  const handleBreadcrumbClick = () => {
    history.push(getDashboardUrl());
  };

  const onHowItWorksClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickHowItWorks(csEventParams);
    setShowHowItWorksModal(true);
  };

  const onLearnMoreClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickLearnMore(csEventParams);
    setShowLearnMoreModal(true);
  };

  const onWhatsCoveredClick = () => {
    OptInCSEvents.COVERAGE_SELECTION.ClickWhatsCovered(csEventParams);
    setShowWhatsCoveredModal(true);
  };

  const handleSelectCoverageOption = () => {
    if (selectedPremiumCoverageOption) {
      Promise.resolve(
        dispatch(
          PropertiesActions.selectRentalProtectionCoverageLevelForListing({
            listingAlias,
            premiumCoverageId: selectedPremiumCoverageOption.premiumCoverageId,
          }),
        ),
      )
        .then(() => {
          return completeLightweightOptIn({ listingAlias });
        })
        .then(() => {
          dispatch(
            updateLocation(
              `${paths.rentGuaranteePage}/properties/${listingAlias}/managePolicies/create-new?showSelectedCoverageToast=true`,
            ),
          );
        })
        .catch(() => {
          setHasApiError(true);
        });
    }
  };

  const renderInfoSection = () => {
    return (
      <Layout.Root css={{ marginInline: { base: '0', lgDown: '400' } }}>
        <Layout.Content css={{ gridColumnEnd: 'span 12', paddingBottom: '0', paddingInline: '0' }}>
          <Paragraph textStyle="body-bold" color="text.subtle">
            How does it work?
          </Paragraph>
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginInline: 'auto' }}>
          <Image src={PropertyDamageIcon} alt="broken property" css={{ width: '44px', height: '44px' }} />
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0' }}>
          <Paragraph textStyle="body-bold">Protect against missed rent and damages</Paragraph>
          <Paragraph textStyle="body-sm" color="text.subtle">
            Gain peace of mind with coverage for things like pet damage, eviction costs, and more.
          </Paragraph>
          <Paragraph>
            <TextButton textStyle="body-sm" onClick={onWhatsCoveredClick}>
              What&apos;s covered
            </TextButton>
          </Paragraph>
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginInline: 'auto' }}>
          <Image src={HomeSparkleIcon} alt="home" css={{ width: '44px', height: '44px' }} />
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0' }}>
          <Paragraph textStyle="body-bold">Use with existing landlord insurance</Paragraph>
          <Paragraph textStyle="body-sm" color="text.subtle">
            Rental Protection picks up where your standard landlord property insurance leaves off.
          </Paragraph>
          <Paragraph>
            <TextButton textStyle="body-sm" onClick={onLearnMoreClick}>
              Learn More
            </TextButton>
          </Paragraph>
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 12', paddingBottom: '0', paddingInline: '0' }}>
          <Paragraph textStyle="body-bold" color="text.subtle">
            What&apos;s next?
          </Paragraph>
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 2', padding: '0', marginInline: 'auto' }}>
          <Image src={FolderIcon} alt="folder" css={{ width: '44px', height: '44px' }} />
        </Layout.Content>
        <Layout.Content css={{ gridColumnEnd: 'span 10', padding: '0' }}>
          {isLowFrictionEnabled ? (
            <div data-testid="WhatsNextLowFrictionDescription">
              <Paragraph textStyle="body-bold">Activate coverage before your lease starts</Paragraph>
              <Paragraph textStyle="body-sm" color="text.subtle">
                Share your lease details and pay for your policy to activate coverage.
              </Paragraph>
            </div>
          ) : (
            <div data-testid="WhatsNextZToolsDescription">
              <Paragraph textStyle="body-bold">Activate Zillow&apos;s free tools</Paragraph>
              <Paragraph textStyle="body-sm" color="text.subtle">
                Rental Protection requires use of Zillow applications, leases and payments.
              </Paragraph>
            </div>
          )}
          <Paragraph>
            <TextButton textStyle="body-sm" onClick={onHowItWorksClick}>
              How it works
            </TextButton>
          </Paragraph>
        </Layout.Content>
      </Layout.Root>
    );
  };

  if (isLoading) {
    return <SpinnerContainer />;
  }

  if (hasApiError) {
    return (
      <Banner tone="critical" closeButton={<CloseButton onImpact />} onClose={() => window.location.reload()}>
        Oops, something went wrong. Please refresh the page and try again
      </Banner>
    );
  }

  return (
    <Page.Root css={{ backgroundColor: 'bg.neutral' }} data-testid="CoverageLevelSelectionPage">
      <Page.Breadcrumb
        css={{
          backgroundColor: 'bg.neutral',
          lgDown: {
            backgroundColor: 'bg.action.soft.default',
            margin: '0',
            paddingBlock: '600',
          },
        }}
      >
        <TextButton
          textStyle="body-bold"
          onClick={handleBreadcrumbClick}
          data-testid="breadcrumb-close-button"
          icon={<IconChevronLeftFilled />}
        >
          Back
        </TextButton>
      </Page.Breadcrumb>
      <Page.Content
        css={{
          paddingInline: '0',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          marginTop: { base: '800', lgDown: 0 },
          marginBottom: '800',
        }}
      >
        <Layout.Root>
          <Layout.Content
            css={{ gridColumnEnd: { base: 'span 7', lgDown: 'span 12' }, padding: { base: '600', lgDown: '0' } }}
          >
            <Box css={{ lgDown: { display: 'none' } }}>
              <ZillowInsuranceServicesLogo />
              <Heading level={1} textStyle="heading-md" fontFamily="heading-alt">
                Rental Protection
              </Heading>
              <Box css={{ marginTop: '600' }}>
                <Heading level={2} textStyle="heading-xs" fontFamily="heading-alt">
                  Insurance for landlords
                </Heading>
                <Paragraph>Choose coverage that works with or replaces your security deposit.</Paragraph>
              </Box>
            </Box>
            <Box css={{ lg: { display: 'none' } }}>
              <Box
                css={{
                  margin: 'auto',
                  width: { base: '520px', lgDown: 'auto' },
                  height: { base: '626px', lgDown: '239px' },
                }}
                style={{ backgroundImage: isScreenMobile ? `url(${BackgroundImageMweb})` : `url(${BackgroundImage})` }}
              >
                <ZillowInsuranceServicesLogo marginX="sm" />
                <Heading level={1} textStyle="heading-md" fontFamily="heading-alt" css={{ marginInline: 'sm' }}>
                  Rental Protection
                </Heading>
                <Box css={{ marginBlock: '200' }}>
                  <Heading level={2} textStyle="heading-xs" fontFamily="heading-alt" css={{ marginInline: 'sm' }}>
                    Insurance for landlords
                  </Heading>
                </Box>
                <Paragraph css={{ marginInline: '400' }}>
                  Choose coverage that works with or replaces your security deposit.
                </Paragraph>
              </Box>
              {renderInfoSection()}
            </Box>
            <Box css={{ marginTop: '600', marginInline: { base: 0, lgDown: '400' } }}>
              <Paragraph css={{ textStyle: 'heading-xs' }}>Select your coverage amount:</Paragraph>
              <Paragraph>You can adjust this before you purchase a policy.</Paragraph>
            </Box>
            {premiumCoverages.map((premiumCoverage: PremiumCoverage, index: number) => {
              return (
                <Box css={{ marginTop: '400', marginInline: { base: 0, lgDown: '400' } }}>
                  <Layout.Root
                    css={{
                      maxWidth: '450px',
                      border: `1px solid {colors.border.subtle}`,
                      borderRadius: '100',
                      cursor: 'pointer',
                    }}
                    onClick={() => setSelectedPremiumCoverageOption(premiumCoverage)}
                  >
                    <Layout.Content css={{ gridColumnEnd: 'span 2' }}>
                      <Radio
                        name={'radio-coverage-option-' + premiumCoverage.coverage}
                        onClick={() => setSelectedPremiumCoverageOption(premiumCoverage)}
                        checked={selectedPremiumCoverageOption?.premiumCoverageId === premiumCoverage.premiumCoverageId}
                      />
                    </Layout.Content>
                    <Layout.Content
                      css={{ gridColumnEnd: { base: 'span 5', lgDown: 'span 6' }, paddingInline: { lgDown: '0' } }}
                    >
                      <Paragraph textStyle="body-sm">Coverage (per lease)</Paragraph>
                      <Paragraph css={{ textStyle: 'heading-sm' }}>
                        {numberUtils.asCurrencyFromCents(premiumCoverage.coverage)}
                      </Paragraph>
                    </Layout.Content>
                    <Layout.Content
                      css={{ gridColumnEnd: { base: 'span 5', lgDown: 'span 4' }, paddingInline: { lgDown: '0' } }}
                    >
                      <Paragraph textStyle="body-sm">Monthly cost^</Paragraph>
                      <Paragraph css={{ textStyle: 'heading-sm' }}>
                        {numberUtils.asCurrencyFromCents(premiumCoverage.premium)}
                      </Paragraph>
                    </Layout.Content>
                  </Layout.Root>
                </Box>
              );
            })}
            <Box css={{ marginTop: '600', marginInline: { base: 0, lgDown: '400' } }}>
              <Box css={{ lg: { display: 'none' } }}>
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={handleSelectCoverageOption}
                  fluid
                  disabled={!selectedPremiumCoverageOption}
                >
                  Go to dashboard
                </Button>
              </Box>
              <Box css={{ lgDown: { display: 'none' } }}>
                <Button
                  tone="brand"
                  emphasis="filled"
                  onClick={handleSelectCoverageOption}
                  disabled={!selectedPremiumCoverageOption}
                >
                  Go to dashboard
                </Button>
              </Box>
              <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginTop: '400' }}>
                No commitment to purchase a policy today.
              </Paragraph>
            </Box>
            <Box css={{ marginTop: '600', marginInline: { base: 0, lgDown: '400' } }}>
              <Paragraph textStyle="body-xs" color="text.subtle" css={{ marginTop: '800' }}>
                ^plus applicable surplus lines taxes and fees
              </Paragraph>
            </Box>
          </Layout.Content>
          <Box css={{ lgDown: { display: 'none' } }}>
            <Layout.Content css={{ gridColumnEnd: { base: 'span 5', mdDown: 'span 12' } }}>
              <Box
                css={{
                  margin: 'auto',
                  width: { base: '520px', lgDown: 'auto' },
                  height: { base: '626px', lgDown: '239px' },
                }}
                style={{ backgroundImage: isScreenMobile ? `url(${BackgroundImageMweb})` : `url(${BackgroundImage})` }}
              >
                <Card
                  tone="neutral"
                  css={{ marginTop: '1200', position: 'absolute', maxWidth: '450px', marginLeft: '-25px' }}
                >
                  {renderInfoSection()}
                </Card>
              </Box>
            </Layout.Content>
          </Box>
          <Layout.Content
            css={{
              gridColumnEnd: { base: 'span 7', lgDown: 'span 12' },
              paddingTop: '0',
              paddingInline: { base: '800', lgDown: '0' },
            }}
          >
            <Box css={{ marginInline: { base: 0, lgDown: '400' } }}>
              <Heading level={4} fontFamily="heading-alt" css={{ marginBottom: '600' }}>
                Learn more
              </Heading>
              <Box css={{ marginBottom: '600' }}>
                <Accordion.Root>
                  <Accordion.Item value="choose-coverage-amount">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        How do I choose my coverage amount?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Rental Protection is available in two coverage levels: (1) $5,000 coverage limit for a $39/month
                        premium or (2) $10,000 coverage limit for a $59/month premium.
                      </Paragraph>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Both options offer the same type of coverage, the only difference is in the coverage limit and
                        monthly cost.
                      </Paragraph>
                      <Paragraph>
                        When considering which coverage limit to select, you may want to consider factors such as:
                      </Paragraph>
                      <List.Root css={{ marginBottom: '400' }}>
                        <List.Item css={{ paddingBlock: '0' }}>
                          <Text asChild>
                            <i>Monthly rent:</i>
                          </Text>{' '}
                          Consider your rent amount and how many months of rent you want to account for. You may want to
                          consider a higher coverage limit if you have higher monthly rent or want to be prepared for
                          several months of missed payments.
                        </List.Item>
                        <List.Item css={{ paddingBlock: '0' }}>
                          <Text asChild>
                            <i>Pets:</i>
                          </Text>{' '}
                          Consider potential sources of property damage your tenant may cause. For example, if your
                          tenant is moving in with pets, you may want to consider a higher coverage limit.
                        </List.Item>
                        <List.Item css={{ paddingBlock: '0' }}>
                          <Text asChild>
                            <i>Security deposit:</i>
                          </Text>{' '}
                          If you&apos;re waiving or reducing your security deposit, you may want to make sure your
                          coverage limit equals at least your typical security deposit amount. If you&apos;re collecting
                          your typical security deposit amount, a lower coverage limit may be appropriate.
                        </List.Item>
                      </List.Root>
                      <Paragraph>
                        You can change your coverage level at any time up to coverage request signing. Once you sign
                        your coverage request, you will no longer be able to change your coverage level.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  {isLowFrictionEnabled ? (
                    <LeaseQualificationAccordion />
                  ) : (
                    <Accordion.Item value="rental-protection-z-tools" data-testid="RentalProtectionZToolsAccordion">
                      <Accordion.Header>
                        <Heading level={5} css={{ textStyle: 'body' }}>
                          Why does Rental Protection require use of Zillow&apos;s tools?
                        </Heading>
                      </Accordion.Header>
                      <Accordion.Panel>
                        <Paragraph css={{ marginBottom: '400' }}>
                          Zillow Insurance Services uses information it receives from Zillow Rental Manager to prepare
                          an insurance request on your behalf for Rental Protection coverage.
                        </Paragraph>
                        <Paragraph css={{ marginBottom: '400' }}>
                          Zillow Insurance Services also sends rental payment information to LeaseLock that can be used
                          to verify a claim for renter default.
                        </Paragraph>
                        <Paragraph>
                          Here&apos;s how the Zillow Rental Manager tools help with Rental Protection:
                        </Paragraph>
                        <List.Root>
                          <List.Item css={{ paddingBlock: '0' }}>
                            <Text textStyle="body-bold">Applications.</Text> Applicants can upload income documents to
                            help you verify tenants have the minimum income-to-rent ratio required for Rental
                            Protection.
                          </List.Item>
                          <List.Item css={{ paddingBlock: '0' }}>
                            <Text textStyle="body-bold">Leases.</Text> Zillow Insurance Services can quickly send your
                            lease terms to LeaseLock.
                          </List.Item>
                          <List.Item css={{ paddingBlock: '0' }}>
                            <Text textStyle="body-bold">Payments.</Text> The record of payment transactions between you
                            and the renter helps track if rent payments are missed, so Zillow Insurance Services can
                            provide that information to LeaseLock for claims processing.
                          </List.Item>
                        </List.Root>
                      </Accordion.Panel>
                    </Accordion.Item>
                  )}
                  <Accordion.Item value="submit-claim">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        How do I submit a claim?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        You can start a claim from your Rental Protection policy details page when logged into your
                        Zillow Rental Manager account. You&apos;ll be redirected to LeaseLock Insurance Services, who
                        will handle your claim directly.
                      </Paragraph>

                      <Paragraph>
                        We&apos;ll provide LeaseLock with some of the required background information, such as the
                        renter&apos;s income docs, the lease they signed, and their payment history. LeaseLock will
                        provide you with instructions about the additional information they need to process your claim.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="premium-increase-claim">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        Will my premium increase if I submit a claim?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Your monthly premium will stay the same for the duration of the coverage period.
                      </Paragraph>

                      <Paragraph>
                        You can only submit one claim per coverage period - after a claim is submitted, you will no
                        longer pay premiums or have coverage.
                      </Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                  <Accordion.Item value="cancel-rental-protection">
                    <Accordion.Header>
                      <Heading level={5} css={{ textStyle: 'body' }}>
                        Can I cancel Rental Protection at any time?
                      </Heading>
                    </Accordion.Header>
                    <Accordion.Panel>
                      <Paragraph css={{ marginBottom: '400' }}>
                        Yes, you can cancel coverage at any time. If you cancel Rental Protection, the coverage period
                        will continue until the end of the month for any month in which the premium has been paid. Any
                        premium paid during the coverage period is fully earned and non-refundable. Premium may be
                        refundable if it is paid for any month after cancellation.
                      </Paragraph>

                      <Paragraph>After the coverage period ends, you must file a claim within 120 days.</Paragraph>
                    </Accordion.Panel>
                  </Accordion.Item>
                </Accordion.Root>
              </Box>
              <Paragraph>
                For more information, visit our{' '}
                <TextButton asChild>
                  <a target="_blank" rel="noopener noreferrer" href={faq}>
                    FAQ page
                  </a>
                </TextButton>{' '}
                or view a{' '}
                <TextButton asChild>
                  <a target="_blank" rel="noopener noreferrer" href={coverageSummaryUrl}>
                    sample coverage summary.
                  </a>
                </TextButton>
              </Paragraph>
            </Box>
          </Layout.Content>
        </Layout.Root>
      </Page.Content>
      {showHowItWorksModal && (
        <RentalProtectionHowDoesItWorkModal
          onClose={() => setShowHowItWorksModal(false)}
          footer={<ModalFooter submitText="Ok" onSubmit={() => setShowHowItWorksModal(false)} />}
          isLowFrictionEnabled={isLowFrictionEnabled}
        />
      )}
      {showWhatsCoveredModal && (
        <RentalProtectionWhatsCoveredModal
          onClose={() => setShowWhatsCoveredModal(false)}
          onSubmit={() => setShowWhatsCoveredModal(false)}
          // @ts-expect-error
          shouldCloseOnOutsideClick={true}
          renderCloseButton={<CloseButton />}
          coverageSummaryUrl={coverageSummaryUrl}
        />
      )}
      {showLearnMoreModal && (
        <RentalProtectionLearnMoreModal
          onClose={() => setShowLearnMoreModal(false)}
          onSubmit={() => setShowLearnMoreModal(false)}
          // @ts-expect-error
          shouldCloseOnOutsideClick={true}
          renderCloseButton={<CloseButton />}
        />
      )}
    </Page.Root>
  );
};

export default CoverageLevelSelection;
